import { useEffect, useState, useContext } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { AppInterval, AppToken } from "../App";
import {
  FlightSectorPendingData,
  TaskStatusRequest,
  TaskStatusResponseData,
} from "../types/api";
import { Button, DateTime, Line, Loader, Text, notify } from "../components";
import CheckBox2 from "../components/common/Checkbox";
import FlightSectorBox from "../components/common/FlightSectorBox";
import TableBox from "../components/common/TableBox";
import { DEFAULT_LIMIT, TS_FLIGHTSECTOR } from "../constants";
import { COLORS } from "../styles/colors";
import { FONT_WEIGHT } from "../styles/fonts";
import { getFlightSectorStatus } from "../utils/apiHelper";
import DisplayModal from "./common/DisplayModel";

interface ISearchForm {
  rowId?: number;
  server?: string;
  description?: string;
  status?: string;
  highLight?: boolean;
}

const FlightSectorPage = () => {
  const [isShowSpinner, setIsShowSpinner] = useState(false);
  const [searchResults, setSearchResults] = useState<TaskStatusResponseData>(
    [],
  );
  const [limit, setLimit] = useState(DEFAULT_LIMIT);
  const [offset, setOffSet] = useState(0);
  const [pendingOffset, setPendingOffSet] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [refreshTime, setRefreshTime] = useState("");
  const [isInit, setIsInit] = useState(true);
  const [highLight, setHighLight] = useState(true);
  const [alertColor, setAlertColor] = useState(true);
  const [pendingResults, setPendingResults] = useState<FlightSectorPendingData>(
    [],
  );
  const [pendingTotal, setPendingTotal] = useState(0);
  const { handleSubmit, setValue } = useForm<ISearchForm>({});
  const { authorizedToken } = JSON.parse(useContext(AppToken));
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPendingModalOpen, setIsPendingModalOpen] = useState(false);
  const [statusQuery, setStatusQuery] = useState("");
  const [pendingQuery, setPendingQuery] = useState("");
  const { frequencyInterval, setFrequencyInterval } = useContext(AppInterval);
  const refreshTimer =
    Number(frequencyInterval.frequencyInterval) > 0
      ? Number(frequencyInterval.frequencyInterval) * 1000
      : 0;

  useEffect(() => {
    const getData = async () => {
      setIsShowSpinner(true);
      setTotalRecords(0);
      setPendingTotal(0);
      setRefreshTime("");
      setFrequencyInterval({
        frequencyInterval: frequencyInterval.frequencyInterval,
        tabName: TS_FLIGHTSECTOR,
      });
      const { errorMessage, response } = await getFlightSectorStatus(
        {
          taskType: TS_FLIGHTSECTOR,
          limit: limit.toString(),
          offset: "0",
          pendingOffset: "0",
          highLight: `${highLight}`,
        },
        authorizedToken,
      );

      if (errorMessage) {
        setIsShowSpinner(false);
        notify({
          message: errorMessage ?? "Error while loading page",
          type: "error",
        });
        return;
      }

      if (response) {
        setSearchResults(response.data.fsStatusData.tasksList);
        setTotalRecords(response.data.fsStatusData.totalCount);
        setRefreshTime(response.data.fsStatusData.refreshTime);
        setPendingTotal(response.data.fsPendingData.totalCount);
        setPendingResults(response.data.fsPendingData.flightSectorPendingData);
        setStatusQuery(response.data.displayQuery.statusQuery);
        setPendingQuery(response.data.displayQuery.pendingQuery);
        setIsShowSpinner(false);
      }
    };
    if (isInit) {
      getData();
    }
  }, [limit, isInit, highLight, setValue]);

  useEffect(() => {
    fetchRefreshResults({
      limit,
      offset,
    });
  }, [highLight]);

  useEffect(() => {
    if (refreshTimer > 0 && TS_FLIGHTSECTOR === frequencyInterval.tabName) {
      console.log(`refreshTimer: ${refreshTimer}`);
      const comInterval = setInterval(fetchRefreshResults, refreshTimer);
      return () => clearInterval(comInterval);
    }
  }, [frequencyInterval]);

  const fetchRefreshResults = async (request: TaskStatusRequest) => {
    setIsShowSpinner(true);
    setIsInit(false);
    if (request && request.limit) {
      setLimit(limit);
    }
    if (request && request.offset) {
      setOffSet(request.offset);
    }
    if (request && request.pendingOffset) {
      setPendingOffSet(request.pendingOffset);
    }
    if (request && request.highLight) {
      setHighLight(request.highLight);
    }
    const modifiedRequest = {
      taskType: request ? request.taskType : TS_FLIGHTSECTOR || TS_FLIGHTSECTOR,
      limit: request
        ? request.limit?.toString()
        : limit.toString() || limit.toString(),
      offset: request
        ? request.offset?.toString()
        : offset.toString() || offset.toString(),
      pendingOffset: request
        ? request.pendingOffset?.toString()
        : pendingOffset.toString() || pendingOffset.toString(),
      highLight: request
        ? request.highLight?.toString()
        : `${highLight}` || `${highLight}`,
    };

    const { errorMessage, response } = await getFlightSectorStatus(
      modifiedRequest,
      authorizedToken,
    );

    if (errorMessage) {
      setIsShowSpinner(false);
      notify({
        message: errorMessage ?? "Error while loading page",
        type: "error",
      });
      return;
    }

    if (response) {
      setSearchResults(response.data.fsStatusData.tasksList);
      setTotalRecords(response.data.fsStatusData.totalCount);
      setRefreshTime(response.data.fsStatusData.refreshTime);
      setPendingTotal(response.data.fsPendingData.totalCount);
      setPendingResults(response.data.fsPendingData.flightSectorPendingData);
      setIsShowSpinner(false);
    }
  };

  const onSubmit: SubmitHandler<ISearchForm> = () => {
    fetchRefreshResults({
      limit,
      offset,
      pendingOffset,
      highLight,
    });
  };

  const handleChecked = () => {
    setHighLight(highLight ? false : true);
  };

  const showColor = () => {
    setAlertColor(!alertColor);
  };

  const openPopup = () => setIsModalOpen(true);
  const openPendingPopup = () => setIsPendingModalOpen(true);

  return (
    <>
      {isShowSpinner ? (
        <Loader
          style={{
            alignItems: "center",
            flex: 1,
          }}
        />
      ) : null}
      <div
        style={{
          width: "auto",
          height: "auto",
          opacity: isShowSpinner ? "0.5" : "1",
          pointerEvents: isShowSpinner ? "none" : "auto",
          position: "relative",
        }}
      >
        {/* ── Upper Part ── */}
        <div
          style={{
            padding: "0 1rem",
            marginBottom: "0.9375rem",
          }}
        >
          <div style={{ display: "flex", flexDirection: "row", width: "100%" }}>
            <div style={{ justifyContent: "space-between" }}>
              <span
                style={{
                  fontSize: "1.175rem",
                  color: COLORS.black,
                  fontWeight: FONT_WEIGHT.bold,
                }}
              >
                <Text>Flight Sector</Text>
              </span>
              <span
                style={{
                  marginLeft: "0.625rem",
                  cursor: "pointer",
                  color: COLORS.blue17,
                  fontSize: "0.875rem",
                  fontWeight: FONT_WEIGHT.medium,
                }}
                onClick={openPopup}
                role="presentation"
              >
                (Display SQL)
                {isModalOpen && (
                  <DisplayModal
                    isOpen
                    setIsOpen={setIsModalOpen}
                    displayQuery={statusQuery}
                    setIsInit={setIsInit}
                  />
                )}
              </span>
            </div>
          </div>
        </div>
        <div
          style={{
            margin: "1.3125rem 0 0.3125rem 0",
            width: "100%",
            display: "flex",
            flexDirection: "row",
            padding: "0 1rem",
          }}
        >
          <span
            style={{
              fontSize: "0.875rem",
              color: COLORS.black1,
              fontWeight: FONT_WEIGHT.regular,
              width: "25%",
            }}
          >
            <Text>{`Refresh Time: ${refreshTime} HKT`}</Text>
          </span>
          <span
            style={{
              fontSize: "0.875rem",
              color: COLORS.black1,
              fontWeight: FONT_WEIGHT.regular,
              width: "25%",
            }}
          >
            <Text>
              Current Time:
              <DateTime />
            </Text>
          </span>
          <span
            style={{
              fontWeight: FONT_WEIGHT.regular,
              color: COLORS.black1,
              fontSize: "0.875rem",
              width: "20%",
            }}
          >
            <CheckBox2
              defaultChecked={alertColor}
              name="showHighLight"
              label="Show Highlight"
              onChange={showColor}
            />
          </span>
          <span
            style={{
              fontWeight: FONT_WEIGHT.regular,
              color: COLORS.black1,
              fontSize: "0.875rem",
              width: "20%",
            }}
          >
            <CheckBox2
              defaultChecked={highLight}
              name="highLight"
              label="Show Highlight Only"
              onChange={handleChecked}
              customStyles={{
                fontWeight: FONT_WEIGHT.medium,
                color: COLORS.black1,
                fontSize: "0.875rem",
                width: "20%",
              }}
            />
          </span>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Button
              id="refresh"
              title="Refresh"
              type="submit"
              variant="secondary"
              style={{
                position: "relative",
                float: "inline-start",
                marginLeft: "0.625rem",
                textAlign: "center",
                height: "45px",
                width: "80px",
              }}
            />
          </form>
        </div>
        <div style={{ padding: "0 1rem" }}>
          <Line
            commonStyles={{
              marginTop: "0.875rem",
              marginBottom: "0.8rem",
            }}
          />
        </div>

        {/* Upper table: task server status (PROD_MQ-AssetSchld / PROD_ExtComm-Receive) */}
        <TableBox
          searchResults={searchResults || []}
          totalRecords={totalRecords}
          limit={limit}
          fetchRefreshResults={fetchRefreshResults}
          alertColor={alertColor}
        />

        <div style={{ padding: "0 1rem" }}>
          <Line
            commonStyles={{
              marginTop: "0.875rem",
              marginBottom: "0.8rem",
            }}
          />
        </div>

        {/* ── Lower Part ── */}
        <div
          style={{
            padding: "0 1rem",
            marginBottom: "0.8rem",
          }}
        >
          <div style={{ display: "flex", flexDirection: "row", width: "100%" }}>
            <div style={{ justifyContent: "space-between" }}>
              <span
                style={{
                  fontSize: "1.175rem",
                  color: COLORS.black,
                  fontWeight: FONT_WEIGHT.bold,
                }}
              >
                <Text>Flight Sector last 1 day Message</Text>
              </span>
              <span
                style={{
                  marginLeft: "0.625rem",
                  cursor: "pointer",
                  color: COLORS.blue17,
                  fontSize: "0.875rem",
                  fontWeight: FONT_WEIGHT.medium,
                }}
                onClick={openPendingPopup}
                role="presentation"
              >
                (Display SQL)
                {isPendingModalOpen && (
                  <DisplayModal
                    isOpen
                    setIsOpen={setIsPendingModalOpen}
                    displayQuery={pendingQuery}
                    setIsInit={setIsInit}
                  />
                )}
              </span>
            </div>
          </div>
        </div>
        <div
          style={{
            margin: "1.3125rem 0 0.3125rem 0",
            width: "100%",
            display: "flex",
            flexDirection: "row",
            padding: "0 1rem",
          }}
        >
          <span
            style={{
              fontWeight: FONT_WEIGHT.medium,
              color: COLORS.black1,
              fontSize: "1rem",
              paddingLeft: "0.1rem",
            }}
          >
            <Text>{`No. of items: ${pendingTotal}`}</Text>
          </span>
        </div>

        {/* Lower table: last 1 day FLA messages with pagination + highlight */}
        <FlightSectorBox
          searchResults={pendingResults || []}
          totalRecords={pendingTotal}
          limit={limit}
          fetchRefreshResults={fetchRefreshResults}
          alertColor={alertColor}
        />
      </div>
    </>
  );
};

export default FlightSectorPage;
